import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { ErrorHandler, inject, NgModule } from '@angular/core';
import { RollbarModule } from '@becksdevteam/rollbar-angular';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { v4 as uuid } from 'uuid';
import packageInfo from '../../../package.json';
import config from '../app.config';
/** Custom Modules */
import { AuthModule } from './auth/auth.module';
import { GlobalErrorHandler } from './classes/global-error-handler.class';
import { ModuleImportOnceGuard } from './classes/module-import-once-guard.class';
/** Interceptors */
import { BlobErrorHttpInterceptor } from './interceptors/blob-error-http.interceptor';
import { FileUploadInterceptor } from './interceptors/file-upload.interceptor';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
/** Services */
import { CustomRendererService } from './services/custom-renderer.service';
import { LoadActionGuardFactoryService } from './services/load-action-guard-factory.service';
import { SocketIoModule } from './sockets/socket-io.module';

@NgModule({
  declarations: [],
  imports: [
    AuthModule.forRoot({
      issuer: config.okta.issuer,
      clientId: config.okta.clientId,
      redirectUri: `${config.bossUrl}/login/callback`,
      postLogoutRedirectUri: `${config.bossUrl}/logout`,
      scopes: ['openid', 'profile', 'email', 'offline_access'],
      responseType: ['token', 'id_token'],
      state: uuid(),
      tokenManager: {
        autoRenew: true,
      },
      /** @see https://github.com/okta/okta-auth-js/blob/master/docs/autoRenew-notice.md */
      services: {
        autoRenew: false,
        autoRemove: false,
      },
      storageManager: {
        token: {
          storageKey: 'bossOktaStorage',
          storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
          useSeparateCookies: true,
        },
        cache: {
          storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
        },
        transaction: {
          storageTypes: ['cookie'],
        },
      },
      pkce: true,
      cookies: {
        sameSite: 'strict',
      },
      devMode: config.name === 'local',
    }),
    InlineSVGModule.forRoot(),
    RollbarModule.forRoot({
      accessToken: config.rollbarAccessToken,
      environment: config.name,
      codeVersion: packageInfo.version,
      captureUncaught: true,
      captureUnhandledRejections: true,
      autoInstrument: {
        log: false,
      },
      payload: {
        environment: config.name,
        client: {
          javascript: {
            source_map_enabled: true,
            code_version: packageInfo.version,
            // Have Rollbar guess which frames the error was
            // thrown from when the browser does not provide line
            // and column numbers.
            guess_uncaught_frames: true,
          },
        },
      },
    }),
    SocketIoModule.forRoot({
      url: config.socket.url,
      options: {
        forceNew: true,
        transports: ['websocket'],
        path: config.socket.path,
        reconnection: true,
        reconnectionAttempts: 9999,
        timeout: 10000,
      },
    }),
  ],
  providers: [
    LoadActionGuardFactoryService,
    CustomRendererService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BlobErrorHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FileUploadInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class CoreModule extends ModuleImportOnceGuard {
  public constructor() {
    const parent = inject(CoreModule, { skipSelf: true, optional: true });

    super(parent);
  }
}
